import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Badge } from "../ui/badge";
import {
  Users,
  UserCheck,
  Stethoscope,
  CreditCard,
} from "lucide-react";
import { useAdminApi } from "../../hooks/useAdminApi";

const StatCard = ({ title, value, icon: Icon, color = "orange", loading }) => (
  <Card>
    <CardContent className="p-6">
      {loading ? (
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-gray-200 rounded w-2/3" />
          <div className="h-8 bg-gray-200 rounded w-1/2" />
        </div>
      ) : (
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500 mb-1">{title}</p>
            <p className="text-3xl font-bold text-gray-900">
              {value ?? "—"}
            </p>
          </div>
          <div className={`p-3 rounded-full bg-${color}-100`}>
            <Icon className={`w-6 h-6 text-${color}-600`} />
          </div>
        </div>
      )}
    </CardContent>
  </Card>
);

const AdminDashboard = () => {
  const { getStats, getSubscriptions } = useAdminApi();
  const [stats, setStats] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [statsLoading, setStatsLoading] = useState(true);
  const [subsLoading, setSubsLoading] = useState(true);

  useEffect(() => {
    getStats()
      .then(({ data }) => setStats(data))
      .catch(() => {})
      .finally(() => setStatsLoading(false));

    getSubscriptions()
      .then(({ data }) => setSubscriptions(Array.isArray(data) ? data : []))
      .catch(() => {})
      .finally(() => setSubsLoading(false));
  }, []);

  return (
    <div className="space-y-8">
      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Enrolled Today"
          value={stats?.enrolled_today}
          icon={UserCheck}
          color="green"
          loading={statsLoading}
        />
        <StatCard
          title="Total Users"
          value={stats?.total_users?.toLocaleString()}
          icon={Users}
          color="blue"
          loading={statsLoading}
        />
        <StatCard
          title="Total Doctors"
          value={stats?.total_doctors}
          icon={Stethoscope}
          color="orange"
          loading={statsLoading}
        />
        <StatCard
          title="Active Subscribers"
          value={stats?.active_subscriptions}
          icon={CreditCard}
          color="emerald"
          loading={statsLoading}
        />
      </div>

      {/* Subscription user table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">User Subscriptions</CardTitle>
        </CardHeader>
        <CardContent>
          {subsLoading ? (
            <div className="space-y-3">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="animate-pulse h-10 bg-gray-100 rounded" />
              ))}
            </div>
          ) : !subscriptions.length ? (
            <p className="text-sm text-gray-500 py-4 text-center">
              No subscription data available.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>Discount</TableHead>
                    <TableHead>Expires</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {subscriptions.map((sub) => (
                    <TableRow key={sub.subscription_id}>
                      <TableCell className="font-medium">{sub.name}</TableCell>
                      <TableCell className="text-gray-500 text-sm">{sub.email || "—"}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="border-orange-200 text-orange-700">
                          {sub.plan || "—"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-green-700 font-medium">
                          {sub.discount_percent != null ? `${sub.discount_percent}%` : "—"}
                        </span>
                      </TableCell>
                      <TableCell>
                        {sub.expires_at
                          ? new Date(sub.expires_at).toLocaleDateString()
                          : "Forever"}
                      </TableCell>
                      <TableCell>
                        <Badge
                          className={
                            sub.is_active
                              ? "bg-green-100 text-green-700 border-green-200"
                              : "bg-red-100 text-red-700 border-red-200"
                          }
                          variant="outline"
                        >
                          {sub.is_active ? "Active" : "Expired"}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminDashboard;
